import { FormikConfig, FormikValues } from "formik"
import React, { useMemo } from "react"
import { useTranslation } from "react-i18next"
import { StyleProp, View, ViewStyle } from "react-native"
import * as Yup from "yup"

import useTheme from "../../features/Theme/ThemeProvider"
import { logger } from "../../utils/logger"
import { FormikTextInput, InputContainerStyle } from "../FormBis"
import { TextInput } from "../TextInput"
import { Body2, Subtitle2 } from "../Texts"
import { InputScreen } from "./InputScreen"

export interface NamesForm {
  firstName: string
  lastName: string
  rppsNumber?: string
  email: string
  otherEmail?: string
}

const defaultNames: NamesForm = {
  firstName: "firstName",
  lastName: "lastName",
  rppsNumber: "rppsNumber",
  email: "email",
  otherEmail: "otherEmail",
}

interface IProps<Values> {
  inputStyle?: StyleProp<ViewStyle>
  viewStyle?: StyleProp<ViewStyle>
  buttonStyle?: StyleProp<ViewStyle>
  formikConfig?: FormikConfig<Values>
  names?: {
    firstName: string
    lastName: string
    rppsNumber?: string
    email: string
    otherEmail?: string
  }
  styles?: InputContainerStyle
  isEmail: string
  isLastName: string
  showRpps?: boolean
  showRppsInfoMessage?: boolean
  userRole?: string
}

export const NamesFormikConfig = <Values extends FormikValues>(options?: {
  onSubmit?: (args: Values) => void
  initialValues?: NamesForm
  validationSchema?: any
  forceRppsValidation?: boolean
}) =>
  ({
    validateOnMount: true,
    initialTouched: options?.forceRppsValidation ? { rppsNumber: true } : undefined,
    onSubmit: options?.onSubmit || logger,
    initialValues: options?.initialValues || {
      firstName: "",
      lastName: "",
      rppsNumber: "",
      email: "",
      otherEmail: "",
    },
    validationSchema:
      options?.validationSchema ||
      Yup.object<NamesForm>({
        firstName: Yup.string().required(),
        lastName: Yup.string().required(),
        rppsNumber: Yup.string().required(),
        email: Yup.string().required(),
        otherEmail: Yup.string(),
      }),
  } as FormikConfig<NamesForm>)

export const NamesScreen = <Values extends FormikValues>({
  formikConfig,
  viewStyle,
  styles,
  buttonStyle,
  names = defaultNames,
  inputStyle,
  isEmail,
  isLastName,
  showRpps = false,
  showRppsInfoMessage = false,
}: IProps<Values>) => {
  const {
    colors: {
      black: { transparent },
      primary,
      white: { highEmphasis: white },
    },
    dimensions: { margin },
  } = useTheme()
  const { t } = useTranslation()
  const style = { backgroundColor: transparent }
  const s = useMemo(
    () => ({
      textView: {
        marginBottom: margin * 2,
        marginHorizontal: margin / 4,
      },
      rppsInfoContainer: {
        backgroundColor: primary,
        padding: margin,
        borderRadius: margin / 2,
        marginBottom: margin,
      },
      rppsInfoText: {
        color: white,
      },
    }),
    [margin, primary, white],
  )

  const InputElem = !!formikConfig ? FormikTextInput : TextInput
  const showText = isLastName !== undefined && !isEmail ? true : undefined

  const formikFirstNameProps = {
    name: names.firstName,
  }
  const formikLastNameProps = {
    name: names.lastName,
  }
  const formikRppsNumberProps = {
    name: names.rppsNumber,
  }
  const formikEmailProps = {
    name: names.email,
  }
  const formikOtherEmailProps = {
    name: names.otherEmail,
  }

  return (
    <InputScreen<Values>
      Inputs={
        <View>
          {!isEmail ? <Subtitle2 style={s.textView}>{t("addMail")}</Subtitle2> : null}
          <InputElem
            changeUnderline={false}
            {...{ style, ...formikFirstNameProps, styles }}
            label={t("fields.label.firstName")}
            style={inputStyle}
            testID="input_firstname"
          />
          <InputElem
            changeUnderline={false}
            {...{ style, ...formikLastNameProps, styles }}
            label={t("fields.label.lastName")}
            style={inputStyle}
            testID="input_lastname"
          />
          {showRpps && (
            <>
              {showRppsInfoMessage && (
                <View style={s.rppsInfoContainer}>
                  <Body2 style={s.rppsInfoText}>{t("rppsInfoMessage")}</Body2>
                </View>
              )}
              <InputElem
                changeUnderline={false}
                {...{ style, ...formikRppsNumberProps, styles }}
                label={t("fields.label.rppsNumber")}
                style={inputStyle}
                keyboardType="numeric"
                maxLength={11}
                autoCapitalize="none"
                testID="input_rpps_number"
              />
            </>
          )}
          <InputElem
            changeUnderline={false}
            {...{ style, ...formikEmailProps, styles }}
            label={t("fields.label.email")}
            testID="input_email"
            style={inputStyle}
          />
          <InputElem
            changeUnderline={false}
            {...{ style, ...formikOtherEmailProps, styles }}
            label={t("fields.label.otherEmail")}
            testID="input_other_email"
            style={inputStyle}
          />
        </View>
      }
      buttonLabel={t("button.save")}
      {...{ viewStyle, formikConfig }}
      buttonStyle={{ style: buttonStyle }}
      isTextStyle={showText}
    />
  )
}
